/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Box, Typography, Grid, Card, CardContent, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, CircularProgress, Select, FormControl, InputLabel,
    LinearProgress, Tooltip, Paper, Avatar, Divider, Collapse
} from '@mui/material';
import {
    IconPlus, IconEdit, IconTrash, IconTarget, IconFlag,
    IconCurrencyDollar, IconCheck, IconX, IconClock, IconCalendar,
    IconBuilding, IconDescription, IconUser, IconChevronDown, IconChevronUp
} from '@tabler/icons';
import { INVOICE_APICALL } from 'helper/api/api';
import { auth, toastifySuccess, toastifyError } from 'helper/Utility';

const MONTHS = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

const emptyForm = {
    projectName: '',
    clientName: '',
    description: '',
    cost: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: 'active'
};

const emptyUpdateForm = {
    description: '',
    addOnPrice: '',
    date: new Date().toISOString().split('T')[0],
};

const freshMilestoneForm = () => ({ title: '', description: '', amount: '' });

const QUILL_MODULES = {
    toolbar: [
        [{ header: [false, 2, 3] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'];

export default function SalespersonDashboard() {
    const authData = auth('salesperson') || auth('admin');
    const token = authData?.token;

    const [projects, setProjects] = useState([]);
    const [summary, setSummary] = useState({
        totalEarned: 0,
        totalInitialCost: 0,
        totalAddOns: 0,
        totalCompletedMilestones: 0,
        totalPendingMilestones: 0,
        target: 0, 
        threshold: 0, 
        remainingTarget: 0, 
        greenFlag: false
    });
    const [loading, setLoading] = useState(false);

    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());

    // Add/Edit project dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [formLoading, setFormLoading] = useState(false);

    // Delete dialog
    const [deleteId, setDeleteId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Add update dialog
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [updateProjectId, setUpdateProjectId] = useState(null);
    const [updateProjectName, setUpdateProjectName] = useState('');
    const [updateForm, setUpdateForm] = useState(emptyUpdateForm);
    const [updateLoading, setUpdateLoading] = useState(false);

    // Expanded updates row
    const [expandedProject, setExpandedProject] = useState(null);

    // ── Milestone state ───────────────────────────────────────────────────────
    const [milestonesDialog, setMilestonesDialog] = useState(false);
    const [milestonesProject, setMilestonesProject] = useState(null);
    const [milestones, setMilestones] = useState([]);

    const [milestoneFormDialog, setMilestoneFormDialog] = useState(false);
    const [milestoneEditMode, setMilestoneEditMode] = useState(false);
    const [milestoneEditId, setMilestoneEditId] = useState(null);
    const [milestoneForm, setMilestoneForm] = useState(freshMilestoneForm());
    const [milestoneFormLoading, setMilestoneFormLoading] = useState(false);

    const [milestoneDeleteId, setMilestoneDeleteId] = useState(null);
    const [milestoneDeleteLoading, setMilestoneDeleteLoading] = useState(false);
    const [milestoneToggleId, setMilestoneToggleId] = useState(null);

    // ── Project breakdown calculation ────────────────────────────────────────
    const calculateProjectBreakdown = (project) => {
        const addOnTotal = (project.updates || []).reduce((s, u) => s + Number(u.addOnPrice || 0), 0);
        const completedMilestones = (project.milestones || [])
            .filter(m => m.status === "completed")
            .reduce((s, m) => s + Number(m.amount || 0), 0);
        const pendingMilestones = (project.milestones || [])
            .filter(m => m.status !== "completed")
            .reduce((s, m) => s + Number(m.amount || 0), 0);
        
        return {
            addOnTotal,
            completedMilestones,
            pendingMilestones,
            // Total Earned = ONLY completed milestones
            totalEarned: completedMilestones,
            // Total Amount = initial + add-ons (what client pays)
            totalAmount: Number(project.initialCost || 0) + addOnTotal,
        };
    };

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const res = await INVOICE_APICALL(
                `salesperson/projects?month=${filterMonth}&year=${filterYear}`,
                'GET', undefined, token
            );
            setProjects(res?.projects || []);
            setSummary(res?.summary || { 
                totalEarned: 0,
                totalInitialCost: 0,
                totalAddOns: 0,
                totalCompletedMilestones: 0,
                totalPendingMilestones: 0,
                target: 0, 
                threshold: 0, 
                remainingTarget: 0, 
                greenFlag: false 
            });
        } catch (e) {
            toastifyError('Failed to load projects');
        }
        setLoading(false);
    };

    useEffect(() => { fetchProjects(); }, [filterMonth, filterYear]);

    const openAdd = () => {
        setEditMode(false);
        setEditId(null);
        setForm({ ...emptyForm, month: filterMonth, year: filterYear });
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditMode(true);
        setEditId(p._id);
        setForm({
            projectName: p.projectName,
            clientName: p.clientName || '',
            description: p.description || '',
            cost: p.initialCost ?? p.cost,
            month: p.month,
            year: p.year,
            status: p.status || 'active'
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!form.projectName || !form.cost) {
            toastifyError('Project name and cost are required'); return;
        }
        if (form.cost <= 0) {
            toastifyError('Cost must be greater than 0'); return;
        }
        setFormLoading(true);
        try {
            if (editMode) {
                await INVOICE_APICALL(`salesperson/projects/update/${editId}`, 'PUT', form, token);
                toastifySuccess('Project updated successfully');
            } else {
                await INVOICE_APICALL('salesperson/projects/create', 'POST', form, token);
                toastifySuccess('Project added successfully');
            }
            setDialogOpen(false);
            fetchProjects();
        } catch (e) {
            toastifyError(e?.response?.data?.message || 'Something went wrong');
        }
        setFormLoading(false);
    };

    const handleDelete = async () => {
        if (!deleteId) { toastifyError("Invalid project ID"); return; }
        setDeleteLoading(true);
        try {
            await INVOICE_APICALL(`salesperson/projects/delete/${deleteId}`, 'DELETE', undefined, token);
            toastifySuccess('Project deleted successfully');
            setDeleteId(null);
            fetchProjects();
        } catch (e) {
            toastifyError(e?.response?.data?.message || 'Delete failed');
        }
        setDeleteLoading(false);
    };

    const openAddUpdate = (p) => {
        setUpdateProjectId(p._id);
        setUpdateProjectName(p.projectName);
        setUpdateForm(emptyUpdateForm);
        setUpdateDialogOpen(true);
    };

    const handleAddUpdate = async () => {
        if (!updateForm.description && !updateForm.addOnPrice) {
            toastifyError('Please enter a description or add-on price'); return;
        }
        setUpdateLoading(true);
        try {
            await INVOICE_APICALL(
                `salesperson/projects/update/${updateProjectId}/add-update`,
                'POST',
                {
                    description: updateForm.description,
                    addOnPrice: updateForm.addOnPrice ? Number(updateForm.addOnPrice) : 0,
                    date: updateForm.date,
                },
                token
            );
            toastifySuccess('Update added successfully');
            setUpdateDialogOpen(false);
            fetchProjects();
        } catch (e) {
            toastifyError(e?.response?.data?.message || 'Failed to add update');
        }
        setUpdateLoading(false);
    };

    // ── Milestone helpers ─────────────────────────────────────────────────────
    const openMilestonesDialog = (project) => {
        setMilestonesProject(project);
        setMilestones(project.milestones || []);
        setMilestonesDialog(true);
    };

    const openAddMilestone = () => {
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setMilestoneEditMode(false);
        setMilestoneEditId(null);
        setMilestoneForm(freshMilestoneForm());
        setMilestoneFormDialog(true);
    };

    const openEditMilestone = (ms) => {
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setMilestoneEditMode(true);
        setMilestoneEditId(ms._id);
        setMilestoneForm({
            title: ms.title || '',
            description: ms.description || '',
            amount: ms.amount || '',
        });
        setMilestoneFormDialog(true);
    };

    const handleMilestoneSubmit = async () => {
        if (!milestoneForm.title || !milestoneForm.amount) {
            toastifyError('Title and amount are required'); return;
        }
        setMilestoneFormLoading(true);
        try {
            const payload = {
                title: milestoneForm.title,
                description: milestoneForm.description,
                amount: Number(milestoneForm.amount),
            };
            if (milestoneEditMode) {
                await INVOICE_APICALL(
                    `salesperson/projects/${milestonesProject._id}/milestones/${milestoneEditId}`,
                    'POST', payload, token
                );
                toastifySuccess('Milestone updated');
            } else {
                await INVOICE_APICALL(
                    `salesperson/projects/${milestonesProject._id}/milestones`,
                    'POST', payload, token
                );
                toastifySuccess('Milestone added');
            }
            setMilestoneFormDialog(false);
            await fetchProjects();
            setMilestonesDialog(false);
        } catch (e) {
            toastifyError(e?.response?.data?.message || 'Something went wrong');
        }
        setMilestoneFormLoading(false);
    };

    const handleMilestoneToggle = async (ms) => {
        if (!ms || !ms._id) return;
        const msId = ms._id;
        setMilestoneToggleId(msId);
        try {
            if (ms.status === 'completed') {
                await INVOICE_APICALL(
                    `salesperson/projects/${milestonesProject._id}/milestones/${msId}`,
                    'POST', { status: 'pending' }, token
                );
                toastifySuccess('Milestone marked as pending');
            } else {
                await INVOICE_APICALL(
                    `salesperson/projects/${milestonesProject._id}/milestones/${msId}/complete`,
                    'POST', null, token
                );
                toastifySuccess('Milestone marked as completed');
            }
            await fetchProjects();
            setMilestonesDialog(false);
        } catch (e) {
            toastifyError('Failed to update milestone status');
        }
        setMilestoneToggleId(null);
    };

    const handleMilestoneDelete = async () => {
        if (!milestoneDeleteId) return;
        setMilestoneDeleteLoading(true);
        try {
            await INVOICE_APICALL(
                `salesperson/projects/${milestonesProject._id}/milestones/${milestoneDeleteId}`,
                'DELETE', null, token
            );
            toastifySuccess('Milestone deleted');
            setMilestoneDeleteId(null);
            await fetchProjects();
            setMilestonesDialog(false);
        } catch (e) {
            toastifyError('Failed to delete milestone');
        }
        setMilestoneDeleteLoading(false);
    };

    const progressPct = summary.target > 0 ? Math.min((summary.totalEarned / summary.target) * 100, 100) : 0;
    const thresholdPct = summary.target > 0 ? (summary.threshold / summary.target) * 100 : 0;

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <IconCheck size="0.8rem" />;
            case 'cancelled': return <IconX size="0.8rem" />;
            default: return <IconClock size="0.8rem" />;
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#1a1a1a', mb: 0.5 }}>
                            Dashboard
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Welcome back, {authData?.firstName} {authData?.lastName}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<IconPlus size="18px" />}
                        onClick={openAdd}
                        sx={{ bgcolor: '#1976d2', textTransform: 'none', px: 3, '&:hover': { bgcolor: '#1565c0' } }}
                    >
                        Add New Project
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={0.5} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ p: 1 }}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Total Earned
                                    </Typography>
                                    <Typography variant="h4" fontWeight={700} sx={{ color: '#1976d2', mt: 0.5 }}>
                                        ${summary.totalEarned.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                        Completed Milestones Only
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: '#e3f2fd', width: 48, height: 48 }}>
                                    <IconCurrencyDollar size="24px" color="#1976d2" />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ p: 1 }}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Target
                                    </Typography>
                                    <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', mt: 0.5 }}>
                                        ${summary.target.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: '#e8f5e9', width: 48, height: 48 }}>
                                    <IconTarget size="24px" color="#2e7d32" />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ p: 1 }}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Threshold
                                    </Typography>
                                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ed6c02', mt: 0.5 }}>
                                        ${summary.threshold.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: '#fff3e0', width: 48, height: 48 }}>
                                    <IconFlag size="24px" color="#ed6c02" />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ p: 1 }}>
                    <Card sx={{
                        borderRadius: 2,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        border: summary.greenFlag ? '1px solid #4caf50' : '1px solid #e8e8e8',
                        bgcolor: summary.greenFlag ? '#f1f8e9' : 'white'
                    }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                        Performance
                                    </Typography>
                                    <Chip
                                        label={summary.greenFlag ? 'Green Flag Achieved!' : 'Below Threshold'}
                                        color={summary.greenFlag ? 'success' : 'warning'}
                                        size="small"
                                        sx={{ mt: 0.2, fontWeight: 600, p: 1 }}
                                    />
                                </Box>
                                <Avatar sx={{ bgcolor: summary.greenFlag ? '#e8f5e9' : '#fff3e0', width: 48, height: 48 }}>
                                    <IconFlag size="24px" color={summary.greenFlag ? '#4caf50' : '#ed6c02'} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Additional Stats - Initial + Add-ons */}
            <Grid container spacing={0.5} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 4 }} sx={{ p: 1 }}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', bgcolor: '#f5f5f5' }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Total Initial Cost
                            </Typography>
                            <Typography variant="h6" fontWeight={600} sx={{ color: '#1976d2' }}>
                                ${summary.totalInitialCost.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }} sx={{ p: 1 }}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', bgcolor: '#f5f5f5' }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Total Add-Ons
                            </Typography>
                            <Typography variant="h6" fontWeight={600} sx={{ color: '#2e7d32' }}>
                                +${summary.totalAddOns.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }} sx={{ p: 1 }}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', bgcolor: '#f5f5f5' }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Pending Milestones
                            </Typography>
                            <Typography variant="h6" fontWeight={600} sx={{ color: '#ed6c02' }}>
                                ${summary.totalPendingMilestones.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Progress */}
            {summary.target > 0 && (
                <Card sx={{ borderRadius: 2, mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8' }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                            Progress to Target (Based on Completed Milestones)
                        </Typography>
                        <Box sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">Achievement</Typography>
                                <Typography variant="body2" fontWeight={600} color={summary.greenFlag ? '#2e7d32' : '#1976d2'}>
                                    {progressPct.toFixed(1)}%
                                </Typography>
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPct}
                                    sx={{
                                        height: 8, borderRadius: 4, bgcolor: '#e0e0e0',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: summary.greenFlag ? '#2e7d32' : '#1976d2', borderRadius: 4,
                                        }
                                    }}
                                />
                                {thresholdPct > 0 && thresholdPct < 100 && (
                                    <Tooltip title={`Threshold: $${summary.threshold.toLocaleString()}`}>
                                        <Box sx={{
                                            position: 'absolute', top: -4, left: `${thresholdPct}%`,
                                            width: 2, height: 16, bgcolor: '#ed6c02', borderRadius: 1,
                                        }} />
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">$0</Typography>
                                <Typography variant="caption" color="text.secondary">Target: ${summary.target.toLocaleString()}</Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Total Earned:</strong> ${summary.totalEarned.toLocaleString()}
                            </Typography>
                            {summary.remainingTarget > 0 ? (
                                <Typography variant="body2" sx={{
                                    color: '#7c2d12', fontWeight: 700, bgcolor: '#fff7ed',
                                    px: 1.5, py: 0.7, borderRadius: 2, border: '1px solid #fdba74'
                                }}>
                                    ${summary.remainingTarget.toLocaleString()} remaining to reach target
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="success.main" fontWeight={500}>
                                    Target achieved! 🎉
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <Paper sx={{ p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: '#fafafa', border: '1px solid #e8e8e8' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={500} sx={{ color: '#666' }}>Filter by:</Typography>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Month</InputLabel>
                        <Select value={filterMonth} label="Month" onChange={e => setFilterMonth(e.target.value)}>
                            {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel>Year</InputLabel>
                        <Select value={filterYear} label="Year" onChange={e => setFilterYear(e.target.value)}>
                            {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {/* Projects Table */}
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e8e8e8' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Projects — {MONTHS.find(m => m.value === filterMonth)?.label} {filterYear}
                            </Typography>
                            <Chip label={`${projects.length} Project${projects.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Project Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Initial</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Add-Ons</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Earned</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Milestones</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 120 }} align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {projects.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                                    No projects found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Click "Add New Project" to create your first project
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : projects.map((p, i) => {
                                        const breakdown = calculateProjectBreakdown(p);
                                        const addOnTotal = breakdown.addOnTotal;
                                        const completedMsTotal = breakdown.completedMilestones;
                                        const pendingMsTotal = breakdown.pendingMilestones;
                                        const totalAmount = breakdown.totalAmount;
                                        const totalEarned = breakdown.totalEarned;
                                        const isExpanded = expandedProject === p._id;
                                        const msCount = (p.milestones || []).length;
                                        const msCompleted = (p.milestones || []).filter(m => m.status === 'completed').length;
                                        
                                        return (
                                            <React.Fragment key={p._id}>
                                                <TableRow hover sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                    <TableCell>{i + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>{p.projectName}</Typography>
                                                        {p.description && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                {p.description}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.clientName ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <IconUser size="14px" color="#666" />
                                                                <Typography variant="body2">{p.clientName}</Typography>
                                                            </Box>
                                                        ) : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600} color="#1976d2">
                                                            ${Number(p.initialCost ?? 0).toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {addOnTotal > 0 ? (
                                                            <Typography variant="body2" fontWeight={600} color="#2e7d32">
                                                                +${addOnTotal.toLocaleString()}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                                        )}
                                                    </TableCell>
                                                    {/* Total column - shows initial + add-ons only */}
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={700} color="#1976d2">
                                                            ${totalAmount.toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    {/* Earned column - shows ONLY completed milestones */}
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={700} color="#ed6c02">
                                                            ${totalEarned.toLocaleString()}
                                                        </Typography>
                                                        {pendingMsTotal > 0 && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                ⏳ ${pendingMsTotal.toLocaleString()} pending
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={getStatusIcon(p.status)}
                                                            label={p.status || 'active'}
                                                            size="small"
                                                            color={p.status === 'completed' ? 'success' : p.status === 'cancelled' ? 'error' : 'primary'}
                                                            variant="outlined"
                                                            sx={{ fontWeight: 500 }}
                                                        />
                                                    </TableCell>
                                                    {/* ── Milestones cell ── */}
                                                    <TableCell>
                                                        <Tooltip title="View Milestones">
                                                            <Chip
                                                                icon={<IconFlag size="0.85rem" />}
                                                                label={msCount > 0 ? `${msCompleted}/${msCount}` : 'View'}
                                                                size="small"
                                                                color={msCount > 0 && msCompleted === msCount ? 'success' : msCount > 0 ? 'warning' : 'default'}
                                                                variant="outlined"
                                                                onClick={() => openMilestonesDialog(p)}
                                                                sx={{ cursor: 'pointer', fontWeight: 600 }}
                                                            />
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Add Update / Add-On Price">
                                                            <IconButton size="small" onClick={() => openAddUpdate(p)} sx={{ color: '#2e7d32' }}>
                                                                <IconPlus size="16px" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {(p.updates || []).length > 0 && (
                                                            <Tooltip title={isExpanded ? 'Hide Updates' : `Show ${p.updates.length} Update(s)`}>
                                                                <IconButton size="small" onClick={() => setExpandedProject(isExpanded ? null : p._id)} sx={{ color: '#1976d2' }}>
                                                                    {isExpanded ? <IconChevronUp size="16px" /> : <IconChevronDown size="16px" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => openEdit(p)} sx={{ color: '#1976d2' }}>
                                                                <IconEdit size="16px" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => setDeleteId(p._id)} sx={{ color: '#d32f2f' }}>
                                                                <IconTrash size="16px" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>

                                                {/* Expanded updates sub-rows */}
                                                {isExpanded && (p.updates || []).length > 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={11} sx={{ p: 0, bgcolor: '#f9fbe7' }}>
                                                            <Box sx={{ px: 3, py: 1.5 }}>
                                                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                                                    Updates / Add-ons
                                                                </Typography>
                                                                <Table size="small" sx={{ mt: 1 }}>
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Date</TableCell>
                                                                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Description</TableCell>
                                                                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Add-On Price</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {p.updates.map((u, ui) => (
                                                                            <TableRow key={ui}>
                                                                                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                                                    {new Date(u.date).toLocaleDateString()}
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontSize: 12 }}>{u.description || '—'}</TableCell>
                                                                                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: u.addOnPrice > 0 ? '#2e7d32' : 'text.secondary' }}>
                                                                                    {u.addOnPrice > 0 ? `+$${Number(u.addOnPrice).toLocaleString()}` : '—'}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                        <TableRow sx={{ bgcolor: '#f0f4ff' }}>
                                                                            <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: 12 }}>Total Add-Ons</TableCell>
                                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#2e7d32' }}>
                                                                                +${addOnTotal.toLocaleString()}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Add/Edit Project Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {editMode ? 'Edit Project' : 'Add New Project'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Project Name *" value={form.projectName}
                                onChange={e => setForm(f => ({ ...f, projectName: e.target.value }))} size="small" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Client Name" value={form.clientName}
                                onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))} size="small" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description" value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                size="small" multiline rows={2} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Initial Cost ($) *" type="number" value={form.cost}
                                onChange={e => setForm(f => ({ ...f, cost: e.target.value }))}
                                size="small" InputProps={{ inputProps: { min: 0 } }}
                                helperText={editMode ? "Base price only; add-ons are separate" : ""} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Month</InputLabel>
                                <Select value={form.month} label="Month" onChange={e => setForm(f => ({ ...f, month: e.target.value }))}>
                                    {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select value={form.year} label="Year" onChange={e => setForm(f => ({ ...f, year: e.target.value }))}>
                                    {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        {editMode && (
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select value={form.status} label="Status" onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                                        <MenuItem value="active">Active</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined" size="small">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" size="small" disabled={formLoading} sx={{ bgcolor: '#1976d2' }}>
                        {formLoading ? <CircularProgress size={20} color="inherit" /> : (editMode ? 'Update' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Update / Add-On Dialog */}
            <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Add Update — <span style={{ color: '#1976d2' }}>{updateProjectName}</span>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add a milestone update, a new add-on price, or any other change to this project.
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={updateForm.description}
                                onChange={e => setUpdateForm(f => ({ ...f, description: e.target.value }))}
                                size="small"
                                multiline
                                rows={3}
                                placeholder="e.g. Completed homepage design, Added extra features, Milestone 2 delivered..."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Add-On Price ($)"
                                type="number"
                                value={updateForm.addOnPrice}
                                onChange={e => setUpdateForm(f => ({ ...f, addOnPrice: e.target.value }))}
                                size="small"
                                InputProps={{ inputProps: { min: 0 } }}
                                helperText="Leave 0 if no extra charge for this update"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Date"
                                type="date"
                                value={updateForm.date}
                                onChange={e => setUpdateForm(f => ({ ...f, date: e.target.value }))}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setUpdateDialogOpen(false)} variant="outlined" size="small">Cancel</Button>
                    <Button onClick={handleAddUpdate} variant="contained" size="small" disabled={updateLoading} sx={{ bgcolor: '#2e7d32' }}>
                        {updateLoading ? <CircularProgress size={20} color="inherit" /> : 'Add Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Milestones Dialog ─────────────────────────────────────────────── */}
            <Dialog open={milestonesDialog} onClose={() => setMilestonesDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography fontWeight={700} variant="h6">
                                {milestonesProject?.projectName} — Milestones
                            </Typography>
                            {milestonesProject?.clientName && (
                                <Typography variant="caption" color="text.secondary">
                                    Client: {milestonesProject.clientName}
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<IconPlus size="0.9rem" />}
                            onClick={openAddMilestone}
                            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, borderRadius: 2, flexShrink: 0 }}
                        >
                            Add Milestone
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1 }}>
                    {milestones.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                No milestones yet. Click "Add Milestone" to create one.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                            {milestones.map((ms) => (
                                <Box key={ms._id} sx={{
                                    border: '1px solid #e0e0e0',
                                    borderLeft: `3px solid ${ms.status === 'completed' ? '#2e7d32' : '#1976d2'}`,
                                    borderRadius: 1.5,
                                    p: 1.5,
                                    bgcolor: ms.status === 'completed' ? '#f1f8f2' : '#fafafa',
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>{ms.title}</Typography>
                                            {ms.description && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    component="div"
                                                    sx={{ mt: 0.25, '& p': { margin: 0 }, '& ul, & ol': { pl: 2, my: 0.25 } }}
                                                    dangerouslySetInnerHTML={{ __html: ms.description }}
                                                />
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                                                <Chip
                                                    label={`$${Number(ms.amount || 0).toLocaleString()}`}
                                                    size="small"
                                                    sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: '0.72rem' }}
                                                />
                                                {ms.completedAt && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        ✅ {new Date(ms.completedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                            <Tooltip title={ms.status === 'completed' ? 'Mark as Pending' : 'Mark as Completed'}>
                                                <Chip
                                                    label={milestoneToggleId === ms._id
                                                        ? '...'
                                                        : ms.status === 'completed' ? '✅ Completed' : '⏳ Pending'}
                                                    size="small"
                                                    color={ms.status === 'completed' ? 'success' : 'default'}
                                                    variant={ms.status === 'completed' ? 'filled' : 'outlined'}
                                                    onClick={() => handleMilestoneToggle(ms)}
                                                    sx={{ cursor: 'pointer', fontWeight: 600, minWidth: 100 }}
                                                    disabled={milestoneToggleId === ms._id}
                                                />
                                            </Tooltip>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => openEditMilestone(ms)}
                                                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 26, height: 26, '&:hover': { bgcolor: '#c8e6c9' } }}>
                                                        <IconEdit size="0.8rem" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => setMilestoneDeleteId(ms._id)}
                                                        sx={{ bgcolor: '#ffebee', color: '#d32f2f', width: 26, height: 26, '&:hover': { bgcolor: '#ffcdd2' } }}>
                                                        <IconTrash size="0.8rem" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMilestonesDialog(false)} variant="outlined" size="small">Close</Button>
                </DialogActions>
            </Dialog>

            {/* ── Add / Edit Milestone Form Dialog ─────────────────────────────── */}
            <Dialog open={milestoneFormDialog} onClose={() => setMilestoneFormDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle fontWeight={700}>{milestoneEditMode ? 'Edit Milestone' : 'Add Milestone'}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Title *"
                                value={milestoneForm.title}
                                onChange={e => setMilestoneForm(f => ({ ...f, title: e.target.value }))}
                                size="small"
                                autoComplete="off"
                                autoFocus
                            />
                        </Grid>

                        {/* ── Rich-text description editor ── */}
                        <Grid item xs={12}>
                            <Typography
                                variant="caption"
                                sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 500 }}
                            >
                                Description
                            </Typography>
                            <Box
                                sx={{
                                    border: '1px solid #c4c4c4',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    '&:focus-within': { borderColor: '#1976d2', boxShadow: '0 0 0 2px rgba(25,118,210,0.15)' },
                                    '& .ql-toolbar': {
                                        borderBottom: '1px solid #e0e0e0',
                                        borderTop: 'none',
                                        borderLeft: 'none',
                                        borderRight: 'none',
                                        bgcolor: '#fafafa',
                                        py: '4px',
                                    },
                                    '& .ql-container': {
                                        border: 'none',
                                        fontSize: '0.875rem',
                                        fontFamily: 'inherit',
                                    },
                                    '& .ql-editor': {
                                        minHeight: '110px',
                                        maxHeight: '220px',
                                        overflowY: 'auto',
                                        py: '10px',
                                        px: '12px',
                                        lineHeight: 1.6,
                                    },
                                    '& .ql-editor.ql-blank::before': {
                                        color: '#aaa',
                                        fontStyle: 'normal',
                                        fontSize: '0.875rem',
                                    },
                                }}
                            >
                                <ReactQuill
                                    theme="snow"
                                    value={milestoneForm.description}
                                    onChange={val => setMilestoneForm(f => ({ ...f, description: val }))}
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                    placeholder="Add a description (supports bold, lists, links…)"
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Amount ($) *"
                                type="number"
                                value={milestoneForm.amount}
                                onChange={e => setMilestoneForm(f => ({ ...f, amount: e.target.value }))}
                                size="small"
                                autoComplete="off"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMilestoneFormDialog(false)} variant="outlined" size="small">Cancel</Button>
                    <Button
                        onClick={handleMilestoneSubmit}
                        variant="contained"
                        size="small"
                        disabled={milestoneFormLoading}
                        sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
                    >
                        {milestoneFormLoading
                            ? <CircularProgress size={16} color="inherit" />
                            : milestoneEditMode ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Milestone Confirm ──────────────────────────────────────── */}
            <Dialog open={!!milestoneDeleteId} onClose={() => setMilestoneDeleteId(null)} maxWidth="xs" fullWidth>
                <DialogTitle fontWeight={700}>Delete Milestone</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this milestone? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMilestoneDeleteId(null)} variant="outlined" size="small">Cancel</Button>
                    <Button onClick={handleMilestoneDelete} variant="contained" color="error" size="small" disabled={milestoneDeleteLoading}>
                        {milestoneDeleteLoading ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Project Confirm Dialog */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Delete Project</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete this project? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteId(null)} variant="outlined" size="small">Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error" size="small" disabled={deleteLoading}>
                        {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}